import { notifications } from '@mantine/notifications';

export interface ApiError {
  response?: {
    status: number;
    data?: {
      error?: string;
      message?: string;
      userState?: string;
      code?: string;
    };
  };
  message?: string;
}

export const handleUserApiError = (error: ApiError, context = '') => {
  console.error(`User API Error ${context}:`, error);

  if (error.response) {
    const { status, data } = error.response;

    switch (status) {
      case 401:
        notifications.show({
          title: 'Authentication Required',
          message: 'Please log in again to continue.',
          color: 'red',
        });
        break;

      case 403: {
        const message = data?.message || data?.error || 'Access denied';
        notifications.show({
          title: 'Access Denied',
          message,
          color: 'orange',
        });
        break;
      }

      case 429:
        notifications.show({
          title: 'Too Many Requests',
          message: 'Please wait a moment before trying again.',
          color: 'orange',
        });
        break;

      case 500:
        notifications.show({
          title: 'Server Error',
          message: 'Something went wrong on our end. Please try again later.',
          color: 'red',
        });
        break;

      default:
        notifications.show({
          title: 'Error',
          message: data?.error || data?.message || `Request failed with status ${status}`,
          color: 'red',
        });
    }
  } else {
    // Network error or other issues
    notifications.show({
      title: 'Connection Error',
      message: 'Unable to connect to the server. Please check your internet connection.',
      color: 'red',
    });
  }
};

export const showSuccessNotification = (title: string, message: string) => {
  notifications.show({
    title,
    message,
    color: 'green',
  });
};

// Original handleApiError function for backward compatibility
interface ApiErrorLegacy extends Error {
  response?: {
    status: number;
    data?: any;
  };
  status?: number;
}

export const handleApiError = (error: ApiErrorLegacy, navigate?: (path: string) => void) => {
  const errorStatus = error.response?.status || error.status;
  const errorMessage = error.message || 'An unexpected error occurred';

  // Handle 404 errors
  if (errorStatus === 404 || errorMessage.includes('404')) {
    if (navigate) {
      navigate('/404');
    }
    return;
  }

  // Handle other types of errors with appropriate messages
  let title = 'Error';
  let message = errorMessage;
  let color: 'red' | 'yellow' | 'blue' = 'red';

  if (errorStatus === 401 || errorStatus === 403) {
    title = 'Authentication Error';
    message = 'Please log in again to continue.';
    color = 'yellow';
  } else if (errorStatus === 429) {
    title = 'Rate Limit Exceeded';
    message = 'Please wait a moment before trying again.';
    color = 'yellow';
  } else if (errorStatus && errorStatus >= 500) {
    title = 'Server Error';
    message = 'The server encountered an error. Please try again later.';
    color = 'blue';
  }

  notifications.show({
    title,
    message,
    color,
    autoClose: 5000,
  });
};
